import {
  DashboardOutlined,
  UserOutlined,
  CarOutlined,
  FileProtectOutlined,
  EnvironmentOutlined,
  QuestionCircleOutlined,
  GiftOutlined,
  TeamOutlined,
  IdcardOutlined,
  BarChartOutlined,
  DollarOutlined,
  GlobalOutlined,
  SettingOutlined,
  BellOutlined,
  TrophyOutlined,
  ShareAltOutlined,
  BankOutlined,
  ScheduleOutlined,
  ShoppingOutlined,
  CloseCircleOutlined,
  RocketOutlined,
  FileTextOutlined,
  SwapOutlined,
  AlertOutlined,
  BulbOutlined,
  AimOutlined,
  AuditOutlined,
  // WalletOutlined, // re-enable with the withdrawals resource below
  PlayCircleOutlined,
  SafetyCertificateOutlined,
  TagOutlined,
  LinkOutlined,
  ThunderboltOutlined,
  MobileOutlined,
  PhoneOutlined,
  ApartmentOutlined,
  StarOutlined,
} from '@ant-design/icons';
import type { ResourceProps } from '@refinedev/core';
import { t } from '../i18n';
import { HIERARCHY_READONLY_USERNAMES } from './accessRules';

export const resources: ResourceProps[] = [
  // ============ TOP-LEVEL (no group) ============
  {
    name: 'dashboard',
    list: '/',
    meta: {
      label: t('nav.dashboard'),
      icon: <DashboardOutlined />,
      service: 'all',
    },
  },
  {
    name: 'admin/live-map',
    list: '/live-map',
    meta: {
      label: t('nav.liveMap'),
      icon: <GlobalOutlined />,
      service: 'taxi',
      permission: { group: 'trips', action: 'read' },
    },
  },

  // ============ ASOSIY (Main) ============
  {
    name: 'group-asosiy',
    meta: {
      label: t('nav.main'),
      isGroup: true,
      service: 'taxi',
    },
  },
  // -- Foydalanuvchilar submenu --
  {
    name: 'users',
    meta: {
      label: t('nav.users'),
      icon: <UserOutlined />,
      parent: 'group-asosiy',
      service: 'taxi',
    },
  },
  {
    name: 'admin/users',
    list: '/users',
    show: '/users/:id',
    meta: {
      label: t('nav.allUsers'),
      icon: <UserOutlined />,
      parent: 'users',
      service: 'taxi',
      permission: { group: 'users', action: 'read' },
    },
  },
  {
    name: 'admin/passengers',
    list: '/passengers',
    show: '/passengers/:id',
    meta: {
      label: t('nav.passengers'),
      icon: <IdcardOutlined />,
      parent: 'users',
      service: 'taxi',
      permission: { group: 'users', action: 'read' },
    },
  },
  {
    name: 'admin/drivers',
    list: '/drivers',
    create: '/drivers/create',
    show: '/drivers/:id',
    meta: {
      label: t('nav.drivers'),
      icon: <CarOutlined />,
      parent: 'users',
      service: 'taxi',
      permission: { group: 'drivers', action: 'read' },
    },
  },
  // -- Самозанятость проверка (Soliq.uz) --
  {
    name: 'admin/self-employment-check',
    list: '/self-employment-check',
    meta: {
      label: 'Самозанятость',
      icon: <SafetyCertificateOutlined />,
      parent: 'users',
      service: 'taxi',
      permission: { group: 'drivers', action: 'read' },
    },
  },
  // -- Hujjatlar --
  {
    name: 'admin/documents',
    list: '/documents',
    meta: {
      label: t('nav.documents'),
      icon: <FileProtectOutlined />,
      parent: 'group-asosiy',
      service: 'taxi',
      permission: { group: 'documents', action: 'read' },
    },
  },
  // -- Sug'urta --
  {
    name: 'admin/insurance',
    list: '/insurance',
    meta: {
      label: t('nav.insurance'),
      icon: <SafetyCertificateOutlined />,
      parent: 'group-asosiy',
      service: 'taxi',
      permission: { group: 'drivers', action: 'read' },
    },
  },
  // -- OSAGO sotib olishlar --
  // List of OSAGO insurance policies purchased through the mobile app.
  // Separate from the insurance-reminders list because that one is
  // vehicle-centric (one row per car, sortable by expiry), while this
  // one is purchase-centric (one row per OsagoPolicy, sortable by
  // status / created date).
  {
    name: 'admin/osago-policies',
    list: '/osago-policies',
    meta: {
      label: 'OSAGO sotib olishlar',
      icon: <SafetyCertificateOutlined />,
      parent: 'group-asosiy',
      service: 'taxi',
      permission: { group: 'drivers', action: 'read' },
    },
  },
  // -- Sayohatlar submenu --
  {
    name: 'trips-group',
    meta: {
      label: t('nav.trips'),
      icon: <EnvironmentOutlined />,
      parent: 'group-asosiy',
      service: 'taxi',
    },
  },
  {
    name: 'admin/trips',
    list: '/trips',
    create: '/trips/create',
    show: '/trips/:tripType/:id',
    edit: '/trips/:tripType/:id/edit',
    meta: {
      label: t('nav.allTrips'),
      icon: <EnvironmentOutlined />,
      parent: 'trips-group',
      service: 'taxi',
      permission: { group: 'trips', action: 'read' },
    },
  },
  {
    name: 'admin/cancelled-trips',
    list: '/cancelled-trips',
    meta: {
      label: t('nav.cancelledTrips'),
      icon: <CloseCircleOutlined />,
      parent: 'trips-group',
      service: 'taxi',
      permission: { group: 'trips', action: 'read' },
    },
  },
  {
    name: 'admin/driver-posted-trips',
    list: '/driver-posted-trips',
    show: '/driver-posted-trips/:id',
    meta: {
      label: t('nav.driverTrips'),
      icon: <SwapOutlined />,
      parent: 'trips-group',
      service: 'taxi',
      permission: { group: 'trips', action: 'read' },
    },
  },
  {
    name: 'admin/yol-yolakay-offers',
    list: '/yol-yolakay-offers',
    meta: {
      label: t('nav.yolYolakayOffers'),
      icon: <AimOutlined />,
      parent: 'trips-group',
      service: 'taxi',
      permission: { group: 'trips', action: 'read' },
    },
  },
  // /driver-trip-bookings hidden from nav per ops request — route still
  // mounted in App.tsx so existing deep links don't 404.

  // ============ MOLIYA (Finance) ============
  {
    name: 'group-moliya',
    meta: {
      label: t('nav.finance'),
      isGroup: true,
      service: 'taxi',
    },
  },
  {
    name: 'taxi/payments',
    list: '/payments',
    meta: {
      label: t('nav.payments'),
      icon: <DollarOutlined />,
      parent: 'group-moliya',
      service: 'taxi',
      permission: { group: 'payments', action: 'read' },
    },
  },
  {
    name: 'admin/pricing',
    list: '/pricing',
    meta: {
      label: t('nav.pricing'),
      icon: <SettingOutlined />,
      parent: 'group-moliya',
      service: 'taxi',
      permission: { group: 'payments', action: 'read' },
    },
  },
  {
    name: 'admin/tariffs',
    list: '/tariffs',
    meta: {
      label: t('nav.tariffs'),
      icon: <TagOutlined />,
      parent: 'group-moliya',
      service: 'taxi',
      permission: { group: 'settings', action: 'read' },
    },
  },
  {
    name: 'taxi/promo-codes',
    list: '/promo-codes',
    create: '/promo-codes/create',
    meta: {
      label: t('nav.promoCodes'),
      icon: <GiftOutlined />,
      parent: 'group-moliya',
      service: 'taxi',
      permission: { group: 'promo', action: 'read' },
    },
  },
  {
    name: 'admin/tracking-links',
    list: '/tracking-links',
    create: '/tracking-links/create',
    show: '/tracking-links/:id',
    meta: {
      label: t('nav.trackingLinks'),
      icon: <LinkOutlined />,
      parent: 'group-moliya',
      service: 'taxi',
      permission: { group: 'tracking-links', action: 'read' },
    },
  },
  {
    name: 'admin/incentives',
    list: '/incentives',
    meta: {
      label: t('nav.incentives'),
      icon: <TrophyOutlined />,
      parent: 'group-moliya',
      service: 'taxi',
      permission: { group: 'payments', action: 'read' },
    },
  },
  {
    name: 'admin/referrals',
    list: '/referrals',
    meta: {
      label: t('nav.referrals'),
      icon: <ShareAltOutlined />,
      parent: 'group-moliya',
      service: 'taxi',
      permission: { group: 'payments', action: 'read' },
    },
  },

  // ============ QOLLAB-QUVVATLASH (Support) ============
  {
    name: 'group-support',
    meta: {
      label: t('nav.support'),
      isGroup: true,
      service: 'taxi',
    },
  },
  {
    name: 'admin/tickets',
    list: '/emergency',
    show: '/emergency/:id',
    identifier: 'emergency',
    meta: {
      label: t('nav.emergency'),
      icon: <AlertOutlined />,
      parent: 'group-support',
      service: 'taxi',
      permission: { group: 'support', action: 'read' },
    },
  },
  {
    name: 'admin/tickets',
    list: '/suggestions',
    show: '/suggestions/:id',
    identifier: 'suggestions',
    meta: {
      label: t('nav.suggestions'),
      icon: <BulbOutlined />,
      parent: 'group-support',
      service: 'taxi',
      permission: { group: 'support', action: 'read' },
    },
  },
  // /chat (Chat ko'ruvchi) hidden from nav per ops request — route still
  // mounted in App.tsx so existing deep links don't 404.
  {
    name: 'taxi/faqs',
    list: '/faq',
    create: '/faq/create',
    edit: '/faq/:id/edit',
    meta: {
      label: t('nav.faq'),
      icon: <QuestionCircleOutlined />,
      parent: 'group-support',
      service: 'taxi',
      permission: { group: 'faq', action: 'read' },
    },
  },

  // ============ SOZLAMALAR (Config) ============
  {
    name: 'group-config',
    meta: {
      label: t('nav.config'),
      isGroup: true,
      service: 'taxi',
    },
  },
  {
    name: 'admin/locations',
    list: '/locations',
    meta: {
      label: t('nav.locations'),
      icon: <EnvironmentOutlined />,
      parent: 'group-config',
      service: 'taxi',
      permission: { group: 'settings', action: 'read' },
    },
  },
  {
    name: 'admin/amenities',
    list: '/amenities',
    meta: {
      label: t('nav.amenities'),
      icon: <StarOutlined />,
      parent: 'group-config',
      service: 'taxi',
      permission: { group: 'settings', action: 'read' },
    },
  },
  {
    name: 'admin/tutorials',
    list: '/tutorials',
    meta: {
      label: t('nav.tutorials'),
      icon: <PlayCircleOutlined />,
      parent: 'group-config',
      service: 'taxi',
      permission: { group: 'settings', action: 'read' },
    },
  },
  {
    name: 'admin/hierarchy',
    list: '/hierarchy',
    meta: {
      label: 'Ilova ekranlari',
      icon: <ApartmentOutlined />,
      parent: 'group-config',
      service: 'taxi',
      permission: { group: 'settings', action: 'read' },
      // Read-only viewers (e.g. scoped operator #805) lack `settings` permission
      // but should still see and open this item — force it visible for them.
      forceVisibleForUsernames: HIERARCHY_READONLY_USERNAMES,
    },
  },
  {
    name: 'admin/car-brands',
    list: '/car-brands',
    meta: {
      label: t('nav.carBrands'),
      icon: <CarOutlined />,
      parent: 'group-config',
      service: 'taxi',
      permission: { group: 'settings', action: 'read' },
    },
  },
  {
    name: 'admin/otp',
    list: '/otp',
    meta: {
      label: t('nav.otp'),
      icon: <MobileOutlined />,
      parent: 'group-config',
      service: 'taxi',
      permission: { group: 'users', action: 'read' },
    },
  },
  {
    name: 'admin/cancellation-reasons',
    list: '/cancellation-reasons',
    create: '/cancellation-reasons/create',
    edit: '/cancellation-reasons/:id/edit',
    meta: {
      label: t('nav.cancellationReasons'),
      icon: <CloseCircleOutlined />,
      parent: 'group-config',
      service: 'taxi',
      permission: { group: 'settings', action: 'read' },
    },
  },
  {
    name: 'admin/faollik-bali',
    list: '/faollik-bali',
    meta: {
      label: t('nav.faollikBali'),
      icon: <ThunderboltOutlined />,
      parent: 'group-config',
      service: 'taxi',
      permission: { group: 'settings', action: 'read' },
    },
  },
  {
    name: 'admin/app-updates',
    list: '/app-updates',
    create: '/app-updates/create',
    edit: '/app-updates/:id/edit',
    meta: {
      label: t('nav.appUpdates'),
      icon: <RocketOutlined />,
      parent: 'group-config',
      service: 'taxi',
      permission: { group: 'settings', action: 'read' },
    },
  },
  {
    name: 'taxi/notifications',
    list: '/notifications',
    meta: {
      label: t('nav.notifications'),
      icon: <BellOutlined />,
      parent: 'group-config',
      service: 'taxi',
      permission: { group: 'settings', action: 'read' },
    },
  },
  {
    name: 'admin/scheduled-notifications',
    list: '/scheduled-notifications',
    edit: '/scheduled-notifications/:id/edit',
    meta: {
      label: 'Rejalashtirilgan bildirishnomalar',
      icon: <ScheduleOutlined />,
      parent: 'group-config',
      service: 'taxi',
      permission: { group: 'settings', action: 'read' },
    },
  },
  {
    name: 'admin/staff',
    list: '/staff',
    create: '/staff/create',
    show: '/staff/:id',
    edit: '/staff/:id/edit',
    meta: {
      label: t('nav.staff'),
      icon: <TeamOutlined />,
      parent: 'group-config',
      service: 'taxi',
      permission: { group: 'staff', action: 'read' },
    },
  },
  {
    name: 'admin/audit-logs',
    list: '/audit-logs',
    meta: {
      label: t('nav.auditLog'),
      icon: <AuditOutlined />,
      parent: 'group-config',
      service: 'taxi',
      permission: { group: 'staff', action: 'read' },
    },
  },
  // Hidden until the backend exists: GET /admin/withdrawals (+approve/reject)
  // is not implemented (404), so the page and its filters are non-functional.
  // Re-enable this resource once the endpoints are built.
  // {
  //   name: 'admin/withdrawals',
  //   list: '/withdrawals',
  //   meta: {
  //     label: t('nav.withdrawals'),
  //     icon: <WalletOutlined />,
  //     parent: 'group-config',
  //     service: 'taxi',
  //     permission: { group: 'payments', action: 'read' },
  //   },
  // },
  {
    name: 'taxi/settings',
    list: '/settings',
    meta: {
      label: t('nav.settings'),
      icon: <SettingOutlined />,
      parent: 'group-config',
      service: 'taxi',
      permission: { group: 'settings', action: 'read' },
    },
  },

  // ============ ANALITIKA ============
  {
    name: 'group-analytics',
    meta: {
      label: t('nav.analytics'),
      isGroup: true,
      service: 'taxi',
    },
  },
  {
    name: 'admin/safarlar-analitikasi',
    list: '/safarlar-analitikasi',
    meta: {
      label: 'Safarlar analitikasi',
      icon: <AimOutlined />,
      parent: 'group-analytics',
      service: 'taxi',
      permission: { group: 'trips', action: 'read' },
    },
  },
  {
    name: 'taxi/analytics',
    list: '/analytics',
    meta: {
      label: t('nav.analyticsPage'),
      icon: <BarChartOutlined />,
      parent: 'group-analytics',
      service: 'taxi',
      permission: { group: 'trips', action: 'read' },
    },
  },
  // Hidden until the backend exists: /admin/reports/{trips,revenue,drivers}
  // are not implemented (404), so the page produces no data / Excel export.
  // Re-enable this resource once the endpoints are built.
  // {
  //   name: 'taxi/reports',
  //   list: '/reports',
  //   meta: {
  //     label: t('nav.reports'),
  //     icon: <FileTextOutlined />,
  //     parent: 'group-analytics',
  //     service: 'taxi',
  //     permission: { group: 'trips', action: 'read' },
  //   },
  // },
  {
    name: 'admin/call-notes-analytics',
    list: '/call-notes-analytics',
    meta: {
      label: t('nav.callNotesAnalytics'),
      icon: <PhoneOutlined />,
      parent: 'group-analytics',
      service: 'taxi',
      permission: { group: 'users', action: 'read' },
    },
  },

  // ============ BUS SERVICE RESOURCES ============
  {
    name: 'group-bus-main',
    meta: {
      label: t('nav.main'),
      isGroup: true,
      service: 'bus',
    },
  },
  {
    name: 'admin/bus/companies',
    list: '/bus/companies',
    create: '/bus/companies/create',
    show: '/bus/companies/:id',
    meta: {
      label: t('nav.busCompanies'),
      icon: <BankOutlined />,
      parent: 'group-bus-main',
      service: 'bus',
      hideForBusCompany: true,
    },
  },
  {
    name: 'bus/company/me',
    list: '/bus/company/me',
    meta: {
      label: t('nav.busCompanyProfile'),
      icon: <BankOutlined />,
      parent: 'group-bus-main',
      service: 'bus',
      busCompanyOnly: true,
    },
  },
  {
    name: 'admin/bus/trips',
    list: '/bus/trips',
    create: '/bus/trips/create',
    show: '/bus/trips/:id',
    edit: '/bus/trips/:id/edit',
    meta: {
      label: t('nav.busTrips'),
      icon: <ScheduleOutlined />,
      parent: 'group-bus-main',
      service: 'bus',
    },
  },
  {
    name: 'admin/bus/bookings',
    list: '/bus/bookings',
    create: '/bus/bookings/create',
    show: '/bus/bookings/:id',
    meta: {
      label: t('nav.bookings'),
      icon: <ShoppingOutlined />,
      parent: 'group-bus-main',
      service: 'bus',
    },
  },

  // Bus finance
  {
    name: 'group-bus-moliya',
    meta: {
      label: t('nav.finance'),
      isGroup: true,
      service: 'bus',
    },
  },
  // bus/payments + bus/promo-codes removed — both pointed at the taxi
  // PaymentList / PromoCodeList components (no bus filter), so the
  // sidebar item silently showed taxi rows. Re-add when there's a
  // bus-specific implementation.
  {
    name: 'bus/reports-extended',
    list: '/bus/reports-extended',
    meta: {
      label: "Hisobotlar (qo'shimcha)",
      icon: <BarChartOutlined />,
      parent: 'group-bus-moliya',
      service: 'bus',
    },
  },
  {
    name: 'bus/reports',
    list: '/bus/reports',
    meta: {
      label: t('nav.reports'),
      icon: <FileTextOutlined />,
      parent: 'group-bus-moliya',
      service: 'bus',
      busCompanyOnly: true,
    },
  },

  // Bus support / FAQ removed — both routes resolved to the shared
  // taxi SupportTicketList / FaqList components without any bus
  // filter. Re-add a bus-specific implementation later.

  // Bus config
  {
    name: 'group-bus-config',
    meta: {
      label: t('nav.config'),
      isGroup: true,
      service: 'bus',
    },
  },
  // bus/notifications removed — the route pointed to the taxi
  // NotificationList. Re-add when there is a real bus-specific
  // notifications screen.
  {
    name: 'bus/access',
    list: '/bus/access',
    meta: {
      label: "Kirish va parollar",
      icon: <TeamOutlined />,
      parent: 'group-bus-config',
      service: 'bus',
      hideForBusCompany: true,
    },
  },
  {
    name: 'bus/delay-sms',
    list: '/bus/delay-sms',
    meta: {
      label: "Kechikish SMS",
      icon: <BellOutlined />,
      parent: 'group-bus-config',
      service: 'bus',
    },
  },
  {
    name: 'bus/feedback-stickers',
    list: '/bus/feedback-stickers',
    meta: {
      label: "QR stikerlar",
      icon: <SettingOutlined />,
      parent: 'group-bus-config',
      service: 'bus',
      hideForBusCompany: true,
    },
  },
  {
    name: 'bus/settings',
    list: '/bus/settings',
    meta: {
      label: t('nav.settings'),
      icon: <SettingOutlined />,
      parent: 'group-bus-config',
      service: 'bus',
      hideForBusCompany: true,
    },
  },

  // Bus analytics
  {
    name: 'group-bus-analytics',
    meta: {
      label: t('nav.analytics'),
      isGroup: true,
      service: 'bus',
    },
  },
  {
    name: 'bus/analytics',
    list: '/bus/analytics',
    meta: {
      label: t('nav.analyticsPage'),
      icon: <BarChartOutlined />,
      parent: 'group-bus-analytics',
      service: 'bus',
    },
  },
];
